import type { JSX, ReactNode } from 'react';
import { useStore } from 'zustand';
import { popoutChatUrl } from '../../../net/channel-api';
import { Dialog } from '../../components/Dialog';
import { Icon, type IconName } from '../../components/Icon';
import { useProject, useRuntime, useUiContext } from '../../context';
import { activityStore } from '../../panels/stream-manager/activityStore';
import { safely } from '../../safe';
import type { SettingsTab } from '../../uiStore';
import { scheduleIcsUrl } from '../settings/ScheduleSection';
import '../dialogs.css';

/** The omp-mirror README in the studio repository. */
export const MIRROR_README_URL = 'https://github.com/OhMyPrime/studio/tree/main/apps/mirror#readme';

function ToolCard({ icon, title, description, children }: { icon: IconName; title: string; description: string; children: ReactNode }): JSX.Element {
  return (
    <section className="tools-card" aria-label={title}>
      <h3 className="tools-card-title">
        <Icon name={icon} /> {title}
      </h3>
      <p className="ui-dim tools-card-text">{description}</p>
      <div className="tools-card-actions">{children}</div>
    </section>
  );
}

/** A URL shown in full with Copy (and Open when it is a page). */
function UrlAction({ url, what, open }: { url: string; what: string; open?: boolean }): JSX.Element {
  const { studio } = useUiContext();
  const copy = (): void =>
    void safely(studio, async () => {
      await navigator.clipboard.writeText(url);
      studio.notify('info', `${what} copied.`);
    }, 'Could not copy');
  return (
    <>
      <span className="ui-selectable ui-mono tools-card-url">{url}</span>
      <div className="ui-row">
        <button type="button" className="ui-btn" aria-label={`Copy ${what.toLowerCase()}`} onClick={copy}>
          <Icon name="copy" /> Copy
        </button>
        {open ? (
          <a className="ui-btn" href={url} target="_blank" rel="noopener noreferrer">
            <Icon name="globe" /> Open
          </a>
        ) : null}
      </div>
    </>
  );
}

/** "Streaming tools": what OhMyPeer works with besides this studio, each with its link or a hint to get one. */
export function StreamingToolsDialog({ onClose }: { onClose(): void }): JSX.Element {
  const { ui } = useUiContext();
  const streaming = useProject((p) => p.output.streaming);
  const portalUrl = useProject((p) => p.channel.portalUrl);
  const slug = useStore(activityStore, (s) => s.channelSlug);
  const guestUrl = useRuntime((r) => r.broadcast.room?.guestUrl ?? '');
  const portal = portalUrl.replace(/\/+$/, '');
  const whipUrl = streaming.mode === 'whip' ? streaming.whipEndpoint.trim() : '';
  const noSlug = 'Available once ompeer.com knows your channel (stream key in Settings → Stream).';

  const openSettings = (tab: SettingsTab): void => {
    onClose();
    ui.getState().openDialog({ type: 'settings', tab });
  };

  return (
    <Dialog
      title="Streaming tools"
      onClose={onClose}
      width={760}
      height={620}
      footer={
        <button type="button" className="ui-btn primary" onClick={onClose}>
          Close
        </button>
      }
    >
      <div className="tools-grid">
        <ToolCard icon="broadcast" title="OBS Studio / any WHIP encoder" description="Send the stream from OBS (WHIP output) or any other WHIP encoder.">
          {whipUrl ? (
            <UrlAction url={whipUrl} what="Ingest URL" />
          ) : (
            <>
              <span className="ui-dim">Choose WHIP in Settings → Stream and enter the endpoint to get an ingest URL.</span>
              <button type="button" className="ui-btn" onClick={() => openSettings('stream')}>
                <Icon name="gear" /> Stream settings
              </button>
            </>
          )}
        </ToolCard>

        <ToolCard icon="swap" title="omp-mirror CLI" description="Re-broadcast Twitch, YouTube or fal.live streams to Perck, with their chat and polls.">
          <a className="ui-btn" href={MIRROR_README_URL} target="_blank" rel="noopener noreferrer">
            <Icon name="link" /> Read the README
          </a>
        </ToolCard>

        <ToolCard icon="window" title="Pop-out chat" description="Your channel's chat on its own page, for a browser source or a second screen.">
          {slug ? <UrlAction url={popoutChatUrl(portalUrl, slug)} what="Chat link" open /> : <span className="ui-dim">{noSlug}</span>}
        </ToolCard>

        <ToolCard icon="check" title="Schedule calendar feed" description="An ICS feed of your stream schedule that viewers subscribe to in their calendar app.">
          {slug ? <UrlAction url={scheduleIcsUrl(streaming.swarmApiUrl, slug)} what="Calendar link" /> : <span className="ui-dim">{noSlug}</span>}
        </ToolCard>

        <ToolCard icon="users" title="Guest link" description="Stream Together: guests open this link to send their camera and microphone into the show.">
          {guestUrl ? (
            <UrlAction url={guestUrl} what="Guest link" />
          ) : (
            <span className="ui-dim">Turn on guests and the shared guest link (Stream Manager → Collaboration) while a room is open.</span>
          )}
        </ToolCard>

        <ToolCard icon="lock" title="Control-plane API keys" description="Stream keys and API keys for the ompeer.com control plane.">
          <a className="ui-btn" href={`${portal}/settings/stream`} target="_blank" rel="noopener noreferrer">
            <Icon name="link" /> Manage keys on the portal
          </a>
        </ToolCard>

        <ToolCard icon="film" title="Replay buffer clips" description="Make a clip saves the last moments of the program as a recording.">
          <button type="button" className="ui-btn" onClick={() => openSettings('channel')}>
            <Icon name="gear" /> Clip settings
          </button>
        </ToolCard>

        <ToolCard icon="snapshot" title="Viewer clips" description="Viewers clip your stream from their player; set who can clip, comment and react.">
          <button type="button" className="ui-btn" onClick={() => openSettings('channel')}>
            <Icon name="gear" /> Viewer clip settings
          </button>
        </ToolCard>
      </div>
    </Dialog>
  );
}
